/**
 * E03-F08-T08 — the client's JD text (extracted from a PDF/DOCX or scraped from a
 * page) → the markdown the «Original JD from client» block renders.
 *
 * Ksenia (20.10, project 166): the JD came out as ONE run-on paragraph with the
 * `●` bullets inside the line. Two causes, two halves of the fix:
 *   1. the PDF extractor joined every text item of a page with a space — line and
 *      paragraph breaks were lost at upload (fixed in `fileExtract.core.ts`);
 *   2. even with breaks kept, a `●`/`•` line is not a markdown bullet, so both
 *      renderers showed it as prose.
 * This function does the second half and also repairs text extracted BEFORE the
 * fix: an inline ` ● ` starts a new bullet item. Pure, idempotent (structuring
 * already structured text is a no-op), so it runs at write (`buildJdCopy`) AND at
 * render time (export + view) — stored sheets are fixed without regenerating.
 *
 * Rules:
 *   - `●` `•` `▪` `◦` `○` `■` `►` `➢` `✓` `·` `–` `—` at the start of a line → `- item`;
 *   - the same glyphs (except `·` `–` `—`, which occur in prose) surrounded by
 *     spaces INSIDE a line start a new bullet line;
 *   - a line that starts lowercase (or follows a line ending with `,`/`-`/`&`) is a
 *     wrap of the previous line/bullet — glued back with a space;
 *   - blank lines (paragraph breaks) are kept, 3+ newlines collapse to one blank.
 * Markdown/HTML that is already structured passes through unchanged.
 */
/** Bullet glyphs recognised at the start of a line. */
const LEADING_BULLET_RE = /^[●•▪◦○■►➢✓·–—]\s*(.*)$/u;
/** Bullet glyphs that, surrounded by spaces mid-line, start a new item. */
const INLINE_BULLET_RE = /[ \t]+[●•▪◦○■►➢✓][ \t]+/gu;
/** Existing markdown bullet. */
const MD_BULLET_RE = /^[-*]\s+\S/;
function isWrapContinuation(prev, line) {
    if (/[,&-]$/.test(prev))
        return true;
    return /^\p{Ll}/u.test(line);
}
export function structureJdText(raw) {
    const text = String(raw ?? '')
        .replace(/\r\n?/g, '\n')
        .replace(/\u00a0/g, ' ');
    if (!text.trim())
        return '';
    // HTML keeps its own block structure (renderers hand it over whole).
    if (/<\/?(p|ul|ol|li|br|h[1-6]|div)\b/i.test(text))
        return text.trim();
    const out = [];
    /** Is the last emitted line part of a block (not a blank separator)? */
    const lastNonBlank = () => {
        const last = out[out.length - 1];
        return last !== undefined && last !== '' ? last : null;
    };
    for (const rawLine of text.split('\n')) {
        // Split a run-on line at inline bullets first (text extracted before the fix).
        const pieces = rawLine.replace(INLINE_BULLET_RE, (m) => `\n${m.trim()} `).split('\n');
        for (const piece of pieces) {
            const line = piece.replace(/[ \t]+/g, ' ').trim();
            if (!line) {
                if (out.length > 0 && out[out.length - 1] !== '')
                    out.push('');
                continue;
            }
            const bullet = line.match(LEADING_BULLET_RE);
            if (bullet) {
                const item = bullet[1].trim();
                if (item)
                    out.push(`- ${item}`);
                continue;
            }
            if (MD_BULLET_RE.test(line) || /^#{1,6}\s/.test(line)) {
                out.push(line);
                continue;
            }
            const prev = lastNonBlank();
            if (prev !== null && !/^#{1,6}\s/.test(prev) && isWrapContinuation(prev, line)) {
                out[out.length - 1] = `${prev} ${line}`;
                continue;
            }
            out.push(line);
        }
    }
    while (out.length > 0 && out[out.length - 1] === '')
        out.pop();
    return out.join('\n');
}
//# sourceMappingURL=jdText.js.map